import { useTranslation } from 'react-i18next';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import WorkplaceMarker from '../../../common/map/marker/WorkplaceMarker';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { DetailsList, SiteDetailsSection } from './DetailsList';
import { WrenchIcon } from '@heroicons/react/24/solid';
import { TabBar, TabBarItem } from 'src/ui/wards/common/tabBar/TabBar';
import { useNavigate } from 'react-router-dom';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useWaLocationService } from 'src/api/wa/service/WaLocationService';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { Workplace } from 'src/api/wa/store/models/workplace';
import { useState } from 'react';

interface DetailsProps {
  onError: (hasError: boolean) => void;
}

const DetailSection: React.FC<{ title: string; site: Workplace; section: SiteDetailsSection }> = ({
  title,
  site,
  section,
}) => (
  <div>
    <h2 className="sticky top-0 border-b border-gray-300 bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-800">
      {title}
    </h2>
    <ul className="divide-y divide-gray-400 bg-white shadow-sm">
      <DetailsList site={site} section={section} />
    </ul>
  </div>
);

function Details({ onError }: DetailsProps) {
  const { t } = useTranslation();
  const { cancelWorkplaceReservation } = useWaWorkplaceService();
  const navigate = useNavigate();
  const { selectedWorkplace } = useWaWorkplaceStore();
  const { user } = useWaUserStore();
  const { updateWaUserLocation } = useWaLocationService();
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);

  const handleCancelReservation = async () => {
    if (isLoading || !selectedWorkplace?.customerId || !selectedWorkplace?.details?.reservations)
      return;

    const reservationUserId = user?.id;
    if (!reservationUserId) return;

    setIsLoading(true);
    const reservationsToCancel = [
      {
        customerId: selectedWorkplace.customerId,
        branchCode: selectedWorkplace.branchCode,
        reservations: selectedWorkplace.details.reservations,
      },
    ];

    try {
      const response = await cancelWorkplaceReservation(reservationsToCancel);
      if (response) {
        notify('wards.sites.cancelReservationSuccess');
      } else {
        notify('wards.sites.cancelReservationFailed');
      }
      updateWaUserLocation();
      navigate(-1);
    } catch (error) {
      notify('wards.sites.cancelReservationFailed');
      console.error(error);
      onError(true);
      setIsLoading(false);
    }
  };

  const handleChangeAll = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.StatusBulkChange].path);
  };

  const handleTargetProduct = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.TargetProductList].path);
  };

  const tabBarItems: TabBarItem[] = [];

  if ((selectedWorkplace?.details?.reservations?.length ?? 0) > 0) {
    tabBarItems.push({
      icon: WrenchIcon,
      title: t('wards.tabBar.cancelReservation'),
      onClick: handleCancelReservation,
      disabled: isLoading,
    });
  }

  tabBarItems.push(
    {
      icon: WrenchIcon,
      title: t('wards.tabBar.changeAll'),
      onClick: handleChangeAll,
    },
    {
      icon: WrenchIcon,
      title: t('wards.tabBar.targetProduct'),
      onClick: handleTargetProduct,
    }
  );

  const detailSections = [
    {
      key: 'information',
      title: t('wards.siteDetails.informationSection'),
      sectionEnum: SiteDetailsSection.information,
    },
    {
      key: 'disasterInformation',
      title: t('wards.siteDetails.disasterInformationSection'),
      sectionEnum: SiteDetailsSection.disasterInformation,
    },
  ];

  const center = {
    lat: selectedWorkplace?.location?.latitude ?? 35.681236,
    lng: selectedWorkplace?.location?.longitude ?? 139.767125,
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex-1 overflow-y-auto">
        {selectedWorkplace && (
          <>
            <div>
              {detailSections.map((sec) => (
                <DetailSection
                  key={sec.key}
                  title={sec.title}
                  site={selectedWorkplace}
                  section={sec.sectionEnum}
                />
              ))}
            </div>
            <div className="h-[250px] w-full">
              <Map
                defaultCenter={center}
                defaultZoom={13}
                mapId="WA_DETAILS_MAP"
                disableDefaultUI={true}
                gestureHandling={'none'}
                className="size-full"
              >
                {selectedWorkplace?.location && (
                  <AdvancedMarker
                    position={{
                      lat: selectedWorkplace.location.latitude,
                      lng: selectedWorkplace.location.longitude,
                    }}
                  >
                    <WorkplaceMarker
                      key={selectedWorkplace.customerId}
                      site={selectedWorkplace}
                      canOpenPopup={true}
                    />
                  </AdvancedMarker>
                )}
              </Map>
            </div>
          </>
        )}
      </div>
      <div className="sticky bottom-0 z-50 bg-gray-100 py-2">
        <TabBar items={tabBarItems} />
      </div>
    </div>
  );
}

export default Details;
